/* Input park: the cell editor converts with the Table round (component-map.md). */
import { Box, FormControl, FormLabel, Input } from '@chakra-ui/react';
import { Text } from '@main/complykit/components/text';
import { Nullable } from '@main/shared/types';
import { FilterMode, isIsEmptyOrIsNotEmpty } from '@main/shared/url-helpers';
import { isEmpty } from '@main/shared/utils/is-empty';
import { CellContext, ColumnDef, Row, SortDirection } from '@tanstack/react-table';
import equal from 'fast-deep-equal';
import { ElementRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod/v4';

import { EditablePopover } from '../../editable';
import { OverflowContainer } from '../../overflow-tooltip';
import { errorToast } from '../../toast';
import { BulkChangeConfigBase, useSyncIsBulkLoading } from '../bulk-actions';
import { createFilterHelper } from '../filters';
import { FilterLabelRow } from '../filters/filter-label-row';
import { FilterModeSelector } from '../filters/filter-mode';
import { CellContainer } from '../shared/cell';
import { CanEditGuard, getEditable, ifBulkEditable } from '../table';
import {
  bulkScalarClearAction,
  BulkScalarEditAction,
  ScalarBulkClearButton,
} from './scalar-bulk-clear';

type EditProps<TData> = {
  onChange: (row: Row<TData>, value: string) => void;
  canEditGuard?: CanEditGuard<TData, string | null | undefined>;
};

export type BulkEmailOnChange<TData> = (
  rows: Row<TData>[],
  value: string | undefined,
  action?: BulkScalarEditAction,
) => unknown;
export type UseBulkEmailChangeHook<TData> = () => readonly [BulkEmailOnChange<TData>, boolean];

type EmailColumnConfig<TData, TFilter, TSort> = {
  id: string;
  header: string;
  pattern?: string;
  edit?: EditProps<TData>;
  getGlobalFilterCondition?(filterValue: string): Nullable<TFilter>;
  getColumnFilterCondition?(filterMode: FilterMode, filterValue: string): Nullable<TFilter>;
  getColumnSort?(sortDirection: SortDirection): TSort;
  bulkEdit?: {
    useBulkChange: UseBulkEmailChangeHook<TData>;
    allowClearValue?: boolean;
    bulkInitialValue?: string;
  } & Omit<BulkChangeConfigBase, 'onClick' | 'wrapper'>;
};

const filterHelper = createFilterHelper({
  filterSchema: z.string(),
  filterFn,
});

function filterFn(
  value: string | undefined | null,
  filterValue: string | null,
  filterMode?: FilterMode,
): boolean {
  if (isIsEmptyOrIsNotEmpty(filterMode)) {
    if (filterMode === FilterMode.IsEmpty) {
      return isEmpty(value);
    }
    if (filterMode === FilterMode.IsNotEmpty) {
      return !isEmpty(value);
    }
  }
  if (!filterValue) {
    return true;
  }

  const isContainingValue = !!value?.toLowerCase().includes(filterValue.toLowerCase());

  if (filterMode === FilterMode.Excludes) {
    return !isContainingValue;
  }

  return isContainingValue;
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const anyValueChanged = <T extends unknown>(
  rows: Row<T>[],
  columnId: string,
  newValue: string | undefined,
): boolean =>
  rows.some((row) => {
    const value = row.getValue(columnId);
    const bothFalsy = !value && !newValue;
    return !bothFalsy && value !== newValue;
  });

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const getBulkInitialValue = <T extends unknown>(
  rows: Row<T>[],
  columnId: string,
): string | undefined => {
  const [firstRow, ...restRows] = rows;
  if (!firstRow) return undefined;

  const value = firstRow.getValue<string>(columnId);
  return restRows.every((row) => equal(row.getValue(columnId), value)) ? value : undefined;
};

export function email<TData, TFilter, TSort>() {
  return ({
    edit,
    bulkEdit,
    pattern,
    ...columnDef
  }: ColumnDef<TData, string | null | undefined> &
    EmailColumnConfig<TData, TFilter, TSort>): ColumnDef<TData, string | null | undefined> => {
    const editable = getEditable(edit);
    const useBulkChange = bulkEdit?.useBulkChange;

    return {
      enableGlobalFilter: false,
      enableColumnFilter: false,
      enableSorting: false,
      sortDescFirst: false,

      ...columnDef,

      cell: (context) => {
        const value = context.getValue();

        if (edit && editable(context.row.original, context)) {
          return <EditableCell edit={edit} context={context} pattern={pattern} />;
        }

        return (
          <CellContainer cell={context.cell} p={0}>
            <OverflowContainer>
              <OverflowContainer.Tooltip
                label={value}
                hasArrow
                placement="top"
                fontSize="sm"
                openDelay={500}
              >
                <Box py={4} px={2}>
                  <Text size="inherit" isTruncated>
                    {value}
                  </Text>
                </Box>
              </OverflowContainer.Tooltip>
            </OverflowContainer>
          </CellContainer>
        );
      },

      filterFn: filterHelper.filterFn,

      meta: {
        ...columnDef.meta,
        columnType: 'email',
        editable,
        name: columnDef.header,
        globalFilterFn: filterFn,
        // Tanstack table filters are untyped. Type safety is enforced via filter/column helpers
        getGlobalFilterCondition: columnDef.getGlobalFilterCondition as never,
        getColumnFilterCondition: columnDef.getColumnFilterCondition as never,
        getColumnSort: columnDef.getColumnSort as never,

        bulkEdit: ifBulkEditable(
          edit,
          bulkEdit,
          () =>
            bulkEdit &&
            useBulkChange && {
              ...bulkEdit,
              id: columnDef.id ?? '',
              editor: (editorRows, trigger, isInMenu, onOpenChange, labelledBy) => (
                <BulkEmailEditor
                  id={columnDef.id ?? ''}
                  key={columnDef.id}
                  trigger={trigger}
                  rows={editorRows}
                  useBulkChange={useBulkChange}
                  allowClearValue={bulkEdit.allowClearValue}
                  defaultValue={getBulkInitialValue(editorRows, columnDef.id ?? '')}
                  isInMenu={isInMenu}
                  onOpenChange={onOpenChange}
                  labelledBy={labelledBy}
                  pattern={pattern}
                />
              ),
            },
        ),

        filter: {
          filterSchema: filterHelper.filterSchema,

          DisplayFilter: ({ column }) => {
            const { t } = useTranslation('ui');

            const mode = filterHelper.getFilterMode(column);
            return (
              <span>
                {isIsEmptyOrIsNotEmpty(mode) ? (
                  mode === FilterMode.IsEmpty ? (
                    t('table.filter.isEmpty')
                  ) : (
                    t('table.filter.isNotEmpty')
                  )
                ) : (
                  <>
                    {mode === FilterMode.Excludes && t('table.filter.notLabel')}
                    {filterHelper.getFilterValue(column)}
                  </>
                )}
              </span>
            );
          },

          EditFilter: ({ column }) => {
            const { t } = useTranslation('ui');

            const mode = filterHelper.getFilterMode(column);
            const isExcluding = mode === FilterMode.Excludes;
            const value = filterHelper.getFilterValue(column);
            const hasInput = !isIsEmptyOrIsNotEmpty(mode);
            return (
              <FormControl p={3}>
                <FormLabel fontWeight="medium" mb={hasInput ? undefined : 0}>
                  <FilterLabelRow label={columnDef.header}>
                    <FilterModeSelector {...filterHelper} column={column} />
                  </FilterLabelRow>
                </FormLabel>
                {hasInput && (
                  <Input
                    autoFocus
                    size="sm"
                    placeholder={t('table.column.text.filterPlacehoder')}
                    borderRadius="base"
                    value={value || ''}
                    aria-label={`${columnDef.header} ${
                      isExcluding ? t('table.filter.excludes') : t('table.filter.includes')
                    }`}
                    onChange={(event) =>
                      filterHelper.setFilterValue(column, event.target.value || null)
                    }
                  />
                )}
              </FormControl>
            );
          },
        },
      },
    };
  };
}

type EditableCellProps<TData> = {
  edit: EditProps<TData>;
  context: CellContext<TData, string | null | undefined>;
  pattern?: string;
};

function EditableCell<TData>({ edit, context, pattern }: EditableCellProps<TData>) {
  const { t } = useTranslation('ui');
  const cellValue = context.getValue();
  const inputRef = useRef<ElementRef<'input'>>(null);

  const onCellUpdate = () => {
    const value = inputRef.current?.value;
    const isValid = inputRef.current?.validity.valid;

    if (!isValid) {
      errorToast(t('table.invalidValue'));
      return;
    }

    if (value !== undefined && value !== cellValue) {
      edit.onChange(context.row, value);
    }
  };

  return (
    <EditablePopover onClose={onCellUpdate}>
      <EditablePopover.Reference>
        <EditablePopover.Trigger>
          <CellContainer cell={context.cell} p={0}>
            <OverflowContainer justify="space-between">
              <OverflowContainer.Tooltip
                hasArrow
                label={cellValue}
                placement="top"
                fontSize="sm"
                openDelay={500}
              >
                <Box py={4} px={2}>
                  <Text size="inherit" isTruncated>
                    {cellValue}
                  </Text>
                </Box>
              </OverflowContainer.Tooltip>
            </OverflowContainer>
          </CellContainer>
        </EditablePopover.Trigger>
      </EditablePopover.Reference>
      <EditablePopover.Content bgColor="gray.25" _dark={{ bgColor: 'gray.700' }}>
        <Input
          type="email"
          pattern={pattern}
          ref={inputRef}
          autoFocus
          size="sm"
          variant="unstyled"
          p={4}
          maxH="50px"
          outline="2px solid"
          outlineColor="chakra-border-color"
          defaultValue={cellValue ?? ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
        />
      </EditablePopover.Content>
    </EditablePopover>
  );
}

type BulkEmailEditorProps<TData> = {
  id: string;
  pattern?: string;
  defaultValue?: string;
  trigger: JSX.Element;
  rows: Row<TData>[];
  useBulkChange: UseBulkEmailChangeHook<TData>;
  allowClearValue?: boolean;
  isInMenu?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  labelledBy?: string;
};

function BulkEmailEditor<TData>({
  id,
  defaultValue = '',
  trigger,
  rows,
  useBulkChange,
  allowClearValue = false,
  isInMenu,
  onOpenChange,
  labelledBy,
  pattern,
}: BulkEmailEditorProps<TData>) {
  const inputRef = useRef<ElementRef<'input'>>(null);
  const skipNextUpdateRef = useRef(false);
  const { t } = useTranslation('ui');
  const defaultInputValue = defaultValue ?? '';

  const [value, setValue] = useState(defaultInputValue);
  useEffect(() => {
    skipNextUpdateRef.current = false;
    setValue(defaultInputValue);
  }, [defaultInputValue]);

  const [onChange, isLoading] = useBulkChange();
  useSyncIsBulkLoading(id, isLoading);

  const onUpdate = async () => {
    if (isLoading) {
      return;
    }

    if (skipNextUpdateRef.current) {
      skipNextUpdateRef.current = false;
      return;
    }

    const nextValue = inputRef.current?.value ?? value;

    if (allowClearValue && nextValue.trim() === '') {
      return;
    }

    const isValid = inputRef.current?.validity.valid;

    if (!isValid) {
      errorToast(t('table.invalidValue'));
      return;
    }

    if (anyValueChanged(rows, id, nextValue) && nextValue !== defaultInputValue) {
      await onChange(rows, nextValue);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      skipNextUpdateRef.current = false;
    }
    onOpenChange?.(isOpen);
  };

  const handleClear = async (setOpen: (isOpen: boolean) => void) => {
    if (isLoading) {
      return;
    }

    skipNextUpdateRef.current = true;

    const didUpdate = await onChange(rows, inputRef.current?.value ?? value, bulkScalarClearAction);
    if (didUpdate !== false) {
      skipNextUpdateRef.current = true;
      setOpen(false);
      return;
    }

    skipNextUpdateRef.current = false;
  };

  return (
    <EditablePopover
      onClose={onUpdate}
      onOpenChange={handleOpenChange}
      openEvent={isInMenu ? 'mousedown' : undefined}
      placement={isInMenu ? 'left-start' : 'top-end'}
      closeOnBlur={false}
    >
      <EditablePopover.Trigger>{trigger}</EditablePopover.Trigger>
      <EditablePopover.Content
        bgColor="gray.25"
        _dark={{ bgColor: 'gray.700' }}
        offset={-1}
        data-testid={`bulk-editor-${id}`}
      >
        {(context) => {
          const input = (
            <Input
              type="email"
              pattern={pattern}
              ref={inputRef}
              autoFocus
              size="sm"
              variant="unstyled"
              py={4}
              pl={4}
              pr={allowClearValue ? 10 : 4}
              maxH="50px"
              outline="2px solid"
              outlineColor="chakra-border-color"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
              placeholder={t('table.column.text.filterPlacehoder')}
              aria-labelledby={labelledBy}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const isValid = e.currentTarget.validity.valid;

                  if (!isValid) {
                    errorToast(t('table.invalidValue'));
                    return;
                  }

                  e.currentTarget.blur();
                }
              }}
            />
          );

          return allowClearValue ? (
            <Box position="relative">
              {input}
              <ScalarBulkClearButton
                isDisabled={isLoading}
                onClick={() => void handleClear(context.setOpen)}
              />
            </Box>
          ) : (
            input
          );
        }}
      </EditablePopover.Content>
    </EditablePopover>
  );
}
